"use client";

import { useState } from "react";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface MahadashaMeta {
  lord: string;
  lord_hi: string;
  years: number;
  ruler: string;
  element: string;
  theme: string;
  upay: string;
}

const MAHADASHA_CYCLE: MahadashaMeta[] = [
  {
    lord: "Ketu",
    lord_hi: "केतु",
    years: 7,
    ruler: "Shadow Planet (South Node)",
    element: "Spiritual Fire",
    theme: "Spiritual detachment, inner awakening, past karma resolution, and sudden life shifts.",
    upay: "Ganesh worship, meditation, and feeding street dogs.",
  },
  {
    lord: "Venus",
    lord_hi: "शुक्र",
    years: 20,
    ruler: "Shukra",
    element: "Water",
    theme:
      "Love, marriage, artistic pursuits, luxury, vehicles, financial prosperity, and comfort.",
    upay: "Lakshmi upasana, wearing clean white clothes, and respectful conduct with women.",
  },
  {
    lord: "Sun",
    lord_hi: "सूर्य",
    years: 6,
    ruler: "Surya",
    element: "Fire",
    theme: "Authority, self-realization, leadership, government connections, and fatherly legacy.",
    upay: "Surya Arghya at sunrise, reciting Gayatri Mantra, and honoring father and elders.",
  },
  {
    lord: "Moon",
    lord_hi: "चंद्र",
    years: 10,
    ruler: "Chandra",
    element: "Water",
    theme:
      "Mental peace, emotional growth, mother's blessings, intuition, and public relationships.",
    upay: "Shiva Abhishek on Mondays, drinking water from silver glass, and caring for mother.",
  },
  {
    lord: "Mars",
    lord_hi: "मंगल",
    years: 7,
    ruler: "Mangal",
    element: "Fire",
    theme: "Courage, physical stamina, real estate acquisitions, leadership, and bold initiatives.",
    upay: "Hanuman Chalisa recitation on Tuesdays and donating blood or red lentils.",
  },
  {
    lord: "Rahu",
    lord_hi: "राहु",
    years: 18,
    ruler: "Shadow Planet (North Node)",
    element: "Air & Smoke",
    theme:
      "Worldly ambition, foreign opportunities, unconventional success, innovation, and intense mental drive.",
    upay: "Bhairav worship, feeding birds, and avoiding ungrounded speculation.",
  },
  {
    lord: "Jupiter",
    lord_hi: "गुरु",
    years: 16,
    ruler: "Brihaspati",
    element: "Ether",
    theme:
      "Higher wisdom, wealth, childbirth, righteous expansion, spiritual mentorship, and academic success.",
    upay: "Honoring teachers and gurus, applying saffron tilak, and offering chana dal on Thursdays.",
  },
  {
    lord: "Saturn",
    lord_hi: "शनि",
    years: 19,
    ruler: "Shani",
    element: "Air",
    theme:
      "Discipline, perseverance, long-term career foundations, karmic justice, and mature endurance.",
    upay: "Shani Chalisa, lighting mustard oil lamp under Peepal tree, and helping laborers.",
  },
  {
    lord: "Mercury",
    lord_hi: "बुध",
    years: 17,
    ruler: "Budh",
    element: "Earth",
    theme: "Business commerce, analytical intellect, communication, writing, trade, and learning.",
    upay: "Feeding green fodder to cows on Wednesdays and Vishnu Sahasranama chanting.",
  },
];

function calculateAntardashaLength(
  mahaYears: number,
  antarYears: number
): { years: number; months: number; days: number } {
  const totalMonths = (mahaYears * antarYears * 12) / 120;
  const y = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  const m = Math.floor(remainingMonths);
  const d = Math.round((remainingMonths - m) * 30);
  return { years: y, months: m, days: d };
}

export default function DashaExplorer() {
  const [selectedLord, setSelectedLord] = useState<string>("Jupiter");

  const currentMaha = MAHADASHA_CYCLE.find((m) => m.lord === selectedLord) || MAHADASHA_CYCLE[6];
  const startIdx = MAHADASHA_CYCLE.findIndex((m) => m.lord === selectedLord);
  const orderedAntardashas = [
    ...MAHADASHA_CYCLE.slice(startIdx),
    ...MAHADASHA_CYCLE.slice(0, startIdx),
  ];

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/dasha-calculator"),
    "/dasha-calculator",
    "en"
  );

  const handleConsult = (antarLord: string) => {
    const prompt = `I want to understand the impact of ${currentMaha.lord} Mahadasha with ${antarLord} Antardasha in Vedic astrology. What should I expect and what are the best remedies?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/dasha-calculator");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-6 sm:p-8 shadow-sm">
      <DashaExplorerHeader />
      <DashaTimelineBar selectedLord={selectedLord} onSelect={setSelectedLord} />
      <MahadashaDetailCard
        currentMaha={currentMaha}
        chatHref={chatHref}
        onConsult={() => handleConsult(currentMaha.lord)}
      />
      <AntardashaGrid currentMaha={currentMaha} orderedAntardashas={orderedAntardashas} />
    </div>
  );
}

function DashaExplorerHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-8">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Interactive Vedic Explorer
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        120-Year Vimshottari Mahadasha &amp; Antardasha Calculator (दशा चक्र)
      </h2>
      <p className="text-body-small text-text-secondary mt-2">
        Click on any planet to explore its Mahadasha duration, life theme, and mathematical
        Antardasha sub-period breakdown.
      </p>
    </div>
  );
}

function DashaTimelineBar({
  selectedLord,
  onSelect,
}: {
  selectedLord: string;
  onSelect: (lord: string) => void;
}) {
  return (
    <div className="overflow-x-auto pb-2 mb-6">
      <div className="flex gap-2 min-w-[700px]">
        {MAHADASHA_CYCLE.map((maha) => {
          const isSelected = maha.lord === selectedLord;
          return (
            <button
              key={maha.lord}
              type="button"
              onClick={() => onSelect(maha.lord)}
              className={`flex-1 min-w-[72px] py-3 px-2 rounded-xl text-center border transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm"
              }`}
            >
              <span
                className={`block text-body-small font-bold ${isSelected ? "text-intent-brand" : "text-text-primary"}`}
              >
                {maha.lord}
              </span>
              <span className="block text-caption font-medium text-text-muted mt-0.5">
                {maha.lord_hi}
              </span>
              <span className="inline-block mt-1 text-eyebrow font-bold px-2 py-0.5 rounded-full bg-surface-raised text-text-secondary">
                {maha.years} yrs
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MahadashaDetailCard({
  currentMaha,
  chatHref,
  onConsult,
}: {
  currentMaha: MahadashaMeta;
  chatHref: string;
  onConsult: () => void;
}) {
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {currentMaha.lord} ({currentMaha.lord_hi}) Mahadasha
            </h3>
            <span className="px-3 py-1 rounded-full text-caption font-bold bg-surface-brand-subtle text-intent-brand">
              {currentMaha.years} Years Total ({Math.round((currentMaha.years / 120) * 100)}% of
              Life)
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Ruler: <strong className="text-text-primary">{currentMaha.ruler}</strong> · Element:{" "}
            <strong className="text-text-primary">{currentMaha.element}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-4 py-2 text-caption font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors"
        >
          Consult on {currentMaha.lord} Dasha ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            Core Life Themes &amp; Influence
          </p>
          <p className="text-body-small text-text-primary leading-relaxed mt-1">
            {currentMaha.theme}
          </p>
        </div>
        <div>
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted">
            Classical Upay &amp; Harmonization
          </p>
          <p className="text-body-small text-text-primary leading-relaxed mt-1">
            {currentMaha.upay}
          </p>
        </div>
      </div>
    </div>
  );
}

function AntardashaGrid({
  currentMaha,
  orderedAntardashas,
}: {
  currentMaha: MahadashaMeta;
  orderedAntardashas: MahadashaMeta[];
}) {
  return (
    <div>
      <h4 className="text-body-small font-bold uppercase tracking-wider text-text-primary mb-3">
        9 Antardasha (Sub-Period) Durations in {currentMaha.lord} Mahadasha
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {orderedAntardashas.map((antar) => {
          const length = calculateAntardashaLength(currentMaha.years, antar.years);
          const durationText = [
            length.years > 0 ? `${length.years} yr${length.years > 1 ? "s" : ""}` : "",
            length.months > 0 ? `${length.months} mo${length.months > 1 ? "s" : ""}` : "",
            length.days > 0 && length.years === 0 ? `${length.days} d` : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={antar.lord}
              className="p-3.5 rounded-xl border border-border-default bg-surface-raised flex items-center justify-between"
            >
              <div>
                <span className="text-body-small font-bold text-text-primary">
                  {currentMaha.lord}–{antar.lord}
                </span>
                <span className="block text-caption text-text-muted mt-0.5">
                  {currentMaha.lord_hi}–{antar.lord_hi}
                </span>
              </div>
              <div className="text-right">
                <span className="inline-block px-2.5 py-1 rounded-md text-caption font-bold bg-surface-raised text-text-primary border border-border-default">
                  {durationText || "18 days"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
